// ===== Resumo / Dashboard inicial =====
import React from 'react';
import { Icon } from './icons.jsx';
import { useStore } from './store.jsx';
import {
  FUNNELS, INSIGHTS, MONTHS,
  STAGES, STAGE_NAMES, TIPO_CLIENTE, TODAY, brlCompact, ganhoNoMes, ritmoDoMes,
} from './data.jsx';
import { saudeDoFunil, etapasDoStore, negociosEmRisco, semProximaAcao } from './paineis.js';

const { useState: useStateDash, useMemo: useMemoDash } = React;

function GreetingHeader() {
  const store = useStore();
  const hour = TODAY.getHours();
  const greet = hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";
  const dateStr = TODAY.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });
  const primeiroNome = (store.me?.nome || "").split(" ")[0];
  return (
    <div className="dash-hero">
      <div>
        <div className="dash-hero-greet">{greet}{primeiroNome ? <>, <strong>{primeiroNome}</strong></> : ""}.</div>
        <div className="dash-hero-date">{dateStr.charAt(0).toUpperCase() + dateStr.slice(1)} · sua carteira hoje</div>
      </div>
      <div className="dash-hero-funnels">
        {FUNNELS.map((f) =>
        <div key={f.id} className="hero-funnel-tag">
            <Icon name={f.icon} size={13} />
            <span>{f.name}</span>
            <span className="cycle">{f.cycle}</span>
          </div>
        )}
      </div>
    </div>);

}

// Pílula de escopo — deixa explícito quando um indicador consolida toda a equipe
// ("Equipe") ou é restrito ao gestor ("Só gestor").
function ScopeBadge({ kind }) {
  if (kind === "gestor") {
    return (
      <span className="scope-badge gestor" title="Indicador restrito ao gestor — visível apenas para quem gere a equipe.">
        <Icon name="shield-check" size={10} /> Só gestor
      </span>);

  }
  return (
    <span className="scope-badge team" title="Consolida os dados de todos os vendedores da equipe.">
      <Icon name="users" size={10} /> Equipe
    </span>);

}

function KPIStat({ icon, label, valor, sub, tone = "neutral", onClick, scope }) {
  return (
    <div className={`kpi-stat tone-${tone} ${onClick ? "clickable" : ""}`} onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}>
      <div className="kpi-ic"><Icon name={icon} size={16} /></div>
      <div className="kpi-body">
        <div className="kpi-label">{label}{scope && <ScopeBadge kind={scope} />}</div>
        <div className="kpi-valor">{valor}</div>
        {sub && <div className="kpi-sub">{sub}</div>}
      </div>
      {onClick && <Icon name="arrow-right" size={14} className="kpi-go" />}
    </div>);

}

function FunnelMiniStrip({ funilId, counts, onOpen }) {
  const max = counts[0]?.count || 1;
  const funnel = FUNNELS.find((f) => f.id === funilId);
  const total = counts.reduce((s, r) => s + r.count, 0);
  const ganho = counts.find((r) => r.stage === "GANHO")?.count || 0;
  return (
    <div className="funnel-strip" onClick={onOpen} role="button" tabIndex={0}>
      <div className="fs-head">
        <Icon name={funnel.icon} size={14} style={{ color: "var(--color-ink-soft)" }} />
        <div className="nm">{funnel.name}</div>
        <div className="sub">{funnel.subtitle}</div>
        <div className="meta">
          <span className="mono"><strong>{total}</strong> ativos</span>
          <span className="mono ok"><strong>{ganho}</strong> ganhos</span>
        </div>
        <Icon name="arrow-right" size={13} style={{ color: "var(--color-ink-faint)" }} />
      </div>
      <div className="fs-bars">
        {counts.map((c) =>
        <div key={c.stage} className="fs-bar" title={`${STAGE_NAMES[c.stage]} · ${c.count}`}>
            <div className="fs-bar-fill" style={{ height: Math.max(8, c.count / max * 100) + "%" }} />
            <div className="fs-bar-label">{STAGES.find((s) => s.id === c.stage)?.short}</div>
            <div className="fs-bar-count">{c.count}</div>
          </div>
        )}
      </div>
    </div>);

}

function AlertasProximos({ onOpenAlerts, onOpenLead, onCreateAlert }) {
  const store = useStore();
  const pending = useMemoDash(() => {
    const sevenDays = TODAY.getTime() + 7 * 24 * 60 * 60 * 1000;
    return store.alerts.
    filter((a) => !a.feito && a.when.getTime() < sevenDays).
    sort((a, b) => a.when - b.when);
  }, [store.alerts]);

  // group by today / amanhã / esta semana
  const buckets = useMemoDash(() => {
    const today = [],tomorrow = [],later = [],late = [];
    const tomorrowDate = new Date(TODAY);tomorrowDate.setDate(TODAY.getDate() + 1);
    pending.forEach((a) => {
      if (a.vencido) late.push(a);else
      if (a.when.toDateString() === TODAY.toDateString()) today.push(a);else
      if (a.when.toDateString() === tomorrowDate.toDateString()) tomorrow.push(a);else
      later.push(a);
    });
    return { late, today, tomorrow, later };
  }, [pending]);

  const renderBucket = (label, items, hint, tone) => items.length === 0 ? null :
  <div className="alert-bucket">
      <div className={`alert-bucket-h tone-${tone}`}>
        <span className="dot" />
        <span className="lbl">{label}</span>
        <span className="cnt">{items.length}</span>
        {hint && <span className="hint">{hint}</span>}
      </div>
      <div className="alert-bucket-list">
        {items.map((a) => {
        const lead = store.leads.find((l) => l.id === a.leadId);
        return (
          <button key={a.id} className="alert-row" onClick={() => lead ? onOpenLead(lead.id) : onOpenAlerts()}>
              <div className={`row-time ${tone}`}>
                <span className="hm">{String(a.when.getHours()).padStart(2, "0") + ":" + String(a.when.getMinutes()).padStart(2, "0")}</span>
              </div>
              <div className="row-body">
                <div className="row-title">{a.tipo}</div>
                <div className="row-meta">
                  {lead && <span><Icon name="circle-dot" size={10} /> {lead.empresa}</span>}
                  <span>· {a.canal}</span>
                </div>
              </div>
              <Icon name="chevron-right" size={13} style={{ color: "var(--color-ink-faint)" }} />
            </button>);

      })}
      </div>
    </div>;


  return (
    <div className="card-section dash-alerts">
      <div className="dash-card-head">
        <div className="title">
          <Icon name="calendar" size={14} />
          Próximos alertas
        </div>
        <div className="sub">{pending.length} pendentes nos próximos 7 dias</div>
        <button className="btn sm" onClick={onCreateAlert}><Icon name="plus" size={12} /> Novo</button>
      </div>
      <div className="dash-card-body">
        {renderBucket("Vencidos", buckets.late, "Resolva imediato", "danger")}
        {renderBucket("Hoje", buckets.today, null, "warn")}
        {renderBucket("Amanhã", buckets.tomorrow, null, "neutral")}
        {renderBucket("Esta semana", buckets.later, null, "muted")}
        {pending.length === 0 &&
        <div className="empty alertas-vazio">
            <Icon name="check-circle-2" size={28} style={{ color: "var(--color-ok)", marginBottom: 6 }} />
            <div style={{ fontWeight: 600 }}>Carteira limpa</div>
            <div style={{ fontSize: 12, marginTop: 2 }}>Sem alertas pendentes nos próximos 7 dias.</div>
          </div>
        }
      </div>
    </div>);

}

function CarteiraClientes({ onOpenClientes, onOpenTipo }) {
  const store = useStore();
  const byTipo = useMemoDash(() => {
    const m = Object.fromEntries(TIPO_CLIENTE.map((t) => [t.id, { ativos: 0, inativos: 0, prospec: 0, total: 0 }]));
    store.clientes.forEach((c) => {
      const b = m[c.tipo];if (!b) return;
      b.total++;
      if (c.status === "ativo") b.ativos++;else
      if (c.status === "inativo") b.inativos++;else
      if (c.status === "prospec") b.prospec++;
    });
    return m;
  }, [store.clientes]);

  const novosSemana = store.clientes.filter(c => {
    const dt = c.criadoEm ? new Date(c.criadoEm) : null;
    return dt && (Date.now() - dt.getTime()) < 7 * 864e5;
  }).length;
  const total = store.clientes.length;

  return (
    <div className="card-section dash-carteira">
      <div className="dash-card-head">
        <div className="title">
          <Icon name="users" size={14} />
          Sua carteira de clientes
        </div>
        <div className="sub"><strong>{total}</strong> clientes{novosSemana > 0 && <> · <span style={{ color: "var(--color-ok)" }}>+{novosSemana} esta semana</span></>}</div>
        <button className="btn sm" onClick={onOpenClientes}>Abrir <Icon name="arrow-right" size={12} /></button>
      </div>
      <div className="dash-card-body">
        <div className="carteira-grid">
          {TIPO_CLIENTE.map((t) => {
            const b = byTipo[t.id];
            return (
              <button key={t.id} className="carteira-row" style={{ borderLeftColor: t.color }} onClick={() => onOpenTipo && onOpenTipo(t.id)}>
                <div className="cr-head">
                  <span className="dot" style={{ background: t.color }} />
                  <span className="lbl">{t.label}</span>
                  <span className="tot mono">{b.total}</span>
                </div>
                <div className="cr-bars">
                  <div className="cr-bar ativos" style={{ flex: b.ativos || 0.1 }} title={`Ativos: ${b.ativos}`}>
                    {b.ativos > 0 && <span>{b.ativos} ativos</span>}
                  </div>
                  <div className="cr-bar prospec" style={{ flex: b.prospec || 0.1 }} title={`Prospec.: ${b.prospec}`}>
                    {b.prospec > 0 && <span>{b.prospec} prospec.</span>}
                  </div>
                  <div className="cr-bar inativos" style={{ flex: b.inativos || 0.1 }} title={`Inativos: ${b.inativos}`}>
                    {b.inativos > 0 && <span>{b.inativos} inativos</span>}
                  </div>
                </div>
              </button>);

          })}
        </div>
      </div>
    </div>);

}

function TopCanais({ onOpenEspecificador }) {
  const store = useStore();
  const top = store.especificadores.slice(0, 4);
  const max = top[0]?.receitaGerada || 1;
  // Com todos em R$ 0 a lista seria ordem alfabetica apresentada como ranking.
  const semReceita = top.length === 0 || !(top[0].receitaGerada > 0);
  return (
    <div className="card-section dash-canais">
      <div className="dash-card-head">
        <div className="title">
          <Icon name="compass" size={14} />
          Canais mais ativos
        </div>
        <div className="sub">Receita gerada por escritórios parceiros</div>
      </div>
      <div className="dash-card-body" style={{ paddingTop: 4 }}>
        {semReceita && <div className="empty canais-vazio">Nenhum canal gerou receita ainda.</div>}
        {!semReceita && top.map((e, i) =>
        <button key={e.id} className="canal-row" onClick={() => onOpenEspecificador && onOpenEspecificador(e.id)}>
            <div className="canal-rank mono">{i + 1}</div>
            <div className="canal-info">
              <div className="nm">{e.nome}</div>
              <div className="meta">{e.tipo} · {e.cidade} · {e.pctRepasse}% repasse</div>
            </div>
            <div className="canal-bar">
              <div className="fill" style={{ width: Math.round(e.receitaGerada / max * 100) + "%" }} />
            </div>
            <div className="canal-val mono">{brlCompact(e.receitaGerada)}</div>
          </button>
        )}
      </div>
    </div>);

}

// Ate 22/09 este card tinha a meta FIXA no proprio componente (R$ 1,4 mi), o
// realizado era TODOS os ganhos de sempre rotulado como "do mes", a marca de
// ritmo ficava em 60% ("dia 26") e o aviso dizia "faltam 5 dias uteis" em
// qualquer dia. E a primeira tela que o comercial abre. Agora: meta vigente do
// banco, ganhos do mes por data de ganho, ritmo e dias restantes reais.
function MetasMes({ onOpen }) {
  const store = useStore();
  const mesId = MONTHS[0].id;
  const ganhosMes = store.leads.filter((l) => ganhoNoMes(l, mesId));
  const ganho = ganhosMes.reduce((s, l) => s + (l.valor || 0), 0);
  const metasMes = store.metasDoMes ? store.metasDoMes(mesId) : store.metas;
  const meta = (metasMes && metasMes.empresa) || 0;
  const temMeta = meta > 0;
  const pct = temMeta ? Math.min(100, Math.round(ganho / meta * 100)) : 0;
  const ganhosCount = ganhosMes.length;
  const rt = ritmoDoMes(mesId);
  const abaixo = temMeta && pct < rt.ritmo;
  return (
    <div className="card-section dash-meta clickable-card" onClick={onOpen} role="button" tabIndex={0} title="Abrir vendas realizadas no período">
      <div className="dash-card-head">
        <div className="title">
          <Icon name="target" size={14} />
          Meta do mês
          <ScopeBadge kind="team" />
        </div>
        <div className="sub">{MONTHS[0].label}</div>
        <Icon name="arrow-right" size={13} style={{ color: "var(--color-ink-faint)" }} />
      </div>
      <div className="dash-card-body">
        <div className="meta-wrap">
          <div className="meta-figures">
            <div className="meta-now mono">{brlCompact(ganho)}</div>
            <div className="meta-goal">
              {temMeta ? <>de <span className="mono">{brlCompact(meta)}</span></> : "sem meta definida"}
            </div>
          </div>
          <div className="meta-bar">
            <div className="meta-fill" style={{ width: pct + "%" }} />
            {temMeta && (
              <div className="meta-mid-mark" style={{ left: rt.ritmo + "%" }}
                title={`Ritmo para bater a meta: ${rt.ritmo}% até hoje (dia ${rt.dia} de ${rt.diasNoMes})`} />
            )}
          </div>
          <div className="meta-row">
            {temMeta ? (
              <>
                <span className="mono"><strong>{pct}%</strong> realizado</span>
                <span style={{ color: abaixo ? "var(--color-warn)" : "var(--color-ok)" }}>
                  {abaixo
                    ? `Abaixo do ritmo · faltam ${rt.diasRestantes} ${rt.diasRestantes === 1 ? "dia" : "dias"} no mês`
                    : "No ritmo ou acima"}
                </span>
              </>
            ) : (
              <span>Defina a meta em Gestão › Metas</span>
            )}
          </div>
          <div className="meta-foot">
            <Icon name="check-circle-2" size={12} />
            <span><strong>{ganhosCount}</strong> vendas realizadas no período · toda a equipe</span>
          </div>
        </div>
      </div>
    </div>);

}

function TarefasDoDia({ onOpenLead, onNav, onOpenTask }) {
  const store = useStore();
  const items = useMemoDash(() => {
    return store.alerts.
    filter((a) => !a.feito && (a.vencido || a.when.toDateString() === TODAY.toDateString())).
    sort((a, b) => b.vencido === a.vencido ? a.when - b.when : b.vencido ? 1 : -1);
  }, [store.alerts]);
  const vencidas = items.filter((a) => a.vencido).length;

  return (
    <div className="tarefas-dia">
      <div className="td-head">
        <div className="td-title"><Icon name="check-circle-2" size={15} /> Tarefas do dia <ScopeBadge kind="team" /></div>
        <div className="td-sub">
          {items.length === 0 ? "Tudo em dia" : <><strong>{items.length}</strong> da equipe para hoje{vencidas > 0 && <> · <span className="td-late">{vencidas} vencida{vencidas > 1 ? "s" : ""}</span></>}</>}
        </div>
        <button className="btn sm" onClick={() => onNav("alerts")}>Ver todas <Icon name="arrow-right" size={12} /></button>
      </div>
      {items.length === 0 ?
      <div className="td-empty"><Icon name="check-circle-2" size={18} style={{ color: "var(--color-ok)" }} /> Nenhuma tarefa para hoje. Carteira limpa.</div> :

      <div className="td-list">
          {items.map((a) => {
          const lead = store.leads.find((l) => l.id === a.leadId);
          const cliente = a.clienteId ? store.getClienteById(a.clienteId) : null;
          const owner = store.users.find((u) => u.id === a.autor) || store.me;
          return (
            <div key={a.id} className={`td-card ${a.vencido ? "late" : ""}`}>
                <button className="td-check" title="Concluir" onClick={() => store.toggleAlertDone(a.id)}></button>
                <button className="td-card-body" onClick={() => onOpenTask ? onOpenTask(a) : onNav("alerts")} title="Abrir tarefa">
                  <div className="td-time">{a.vencido ? "Vencida" : String(a.when.getHours()).padStart(2, "0") + ":" + String(a.when.getMinutes()).padStart(2, "0")}</div>
                  <div className="td-task">{a.tipo}</div>
                  <div className="td-meta">{(cliente?.nome || lead?.empresa) && <><Icon name="circle-dot" size={10} /> {cliente?.nome || lead?.empresa} · </>}{a.canal}</div>
                  <div className="td-owner" title={`Responsável: ${owner.nome}`}><span className="td-owner-av">{owner.initials}</span>{owner.nome.split(" ")[0]}</div>
                </button>
              </div>);

        })}
        </div>
      }
    </div>);

}

function ResumoPage({ onOpenLead, onNav, onCreateAlert, onOpenTask, onOpenCliente, onOpenMeta }) {
  const store = useStore();
  const meusLeadsAtivos = store.leads.filter((l) => !l.perdido && l.stage !== "GANHO").length;
  const valorEmJogo = store.leads.filter((l) => !l.perdido && l.stage !== "GANHO").reduce((s, l) => s + l.valor, 0);
  const pendingRepasseLeads = store.leads.filter((l) => l.repasse?.status === "Pendente");
  const repasses = pendingRepasseLeads.reduce((s, l) => s + l.repasse.valorRepasse, 0);
  const repasseCanais = new Set(pendingRepasseLeads.map((l) => l.especificadorId).filter(Boolean)).size;
  const clientesAtivos = store.clientes.filter((c) => c.status === "ativo").length;
  // Inativação automática só vale para perfis recorrentes (canais e lojistas);
  // clientes de projeto não recorrem e por isso não entram nesta contagem.
  const recorrentesInativos = store.clientes.filter((c) => c.status === "inativo" && (c.isCanal || c.tipo === "lojista")).length;

  // Insights consolidados (visão de gestor). Valores de repasse e carteira inativa
  // são derivados em runtime para baterem com os KPIs acima.
  // i1 usa a mesma regua do Indice de Atrito (saudeDoFunil); i2 conta os
  // negocios em aberto sem proxima acao -- campo que passou a gravar em 22/09.
  const saude = useMemoDash(() => saudeDoFunil(store.leads, etapasDoStore(store)), [store.leads, store.etapasPorFunil]);
  const risco = negociosEmRisco(saude);
  const semAcao = semProximaAcao(store.leads);
  const insights = INSIGHTS.map((i) => {
    if (i.id === "i1") return {
      ...i,
      tone: risco.quantidade ? "warn" : "ok",
      valor: `${risco.quantidade} ${risco.quantidade === 1 ? "negócio" : "negócios"}`,
      delta: risco.quantidade ? `${brlCompact(risco.valor)} em jogo` : "nenhum parado além do ritmo",
    };
    if (i.id === "i2") return {
      ...i,
      tone: semAcao.quantidade ? "warn" : "ok",
      valor: `${semAcao.quantidade} de ${semAcao.total}`,
      delta: semAcao.quantidade ? "sem próximo passo combinado" : "todos com próximo passo",
    };
    if (i.id === "i3") return { ...i, valor: brlCompact(repasses), delta: `${repasseCanais} ${repasseCanais === 1 ? "canal aguardando" : "canais aguardando"}` };
    if (i.id === "i4") return { ...i, valor: `${recorrentesInativos} ${recorrentesInativos === 1 ? "perfil" : "perfis"}`, delta: "sem compra recente" };
    return i;
  });

  const INSIGHT_NAV = {
    i1: () => onNav("reports"),
    i2: () => onNav("pipeline"),
    i3: () => onNav("reports"),
    i4: () => onNav("clientes", { status: "inativo" })
  };

  return (
    <div className="main main-scroll dash resumo-page" data-screen-label="Resumo">
      <GreetingHeader />

      <TarefasDoDia onOpenLead={onOpenLead} onNav={onNav} onOpenTask={onOpenTask} />

      {/* KPI strip */}
      <div className="kpi-strip">
        <KPIStat icon="users" label="Clientes ativos" valor={clientesAtivos} sub={`de ${store.clientes.length} na carteira`} tone="accent" onClick={() => onNav("clientes", { status: "ativo" })} />
        <KPIStat icon="layout-kanban" label="Negócios ativos" valor={meusLeadsAtivos} sub="em ambos os funis" onClick={() => onNav("pipeline")} />
        <KPIStat icon="trending-up" label="Valor em pipeline" valor={brlCompact(valorEmJogo)} sub="em aberto · ambos os funis" tone="ok" onClick={() => onNav("pipeline")} />
        <KPIStat icon="wallet" label="Repasses pendentes" valor={brlCompact(repasses)} sub="canais · aguardando pagamento" tone="warn" scope="team" onClick={() => onNav("reports")} />
      </div>

      {/* Insights */}
      <div className="insights-strip">
        {insights.map((i) => {
          const target = INSIGHT_NAV[i.id] || (() => onNav("reports"));
          return (
            <button key={i.id} className={`insight-card tone-${i.tone}`} onClick={target} title={i.hint}>
              <div className="insight-top">
                <div className="insight-ic"><Icon name={i.icon} size={14} /></div>
                {i.scope && <ScopeBadge kind={i.scope} />}
              </div>
              <div className="insight-titulo">{i.titulo}</div>
              <div className="insight-valor mono">{i.valor}</div>
              <div className="insight-delta">{i.delta}</div>
            </button>);

        })}
      </div>

      {/* Main grid */}
      <div className="dash-grid">
        <div className="dash-col-l">
          <div className="dash-row-2">
            <FunnelMiniStrip funilId="projetos" counts={store.contagemDoFunil?.projetos || []} onOpen={() => {store.setActiveFunnel("projetos");onNav("pipeline");}} />
            <FunnelMiniStrip funilId="lojistas" counts={store.contagemDoFunil?.lojistas || []} onOpen={() => {store.setActiveFunnel("lojistas");onNav("pipeline");}} />
          </div>
          <CarteiraClientes
            onOpenClientes={() => onNav("clientes")}
            onOpenTipo={(tipo) => onNav("clientes", { tipo })} />
          
          <TopCanais onOpenEspecificador={(id) => onOpenCliente ? onOpenCliente(id) : onNav("reports")} />
        </div>
        <div className="dash-col-r">
          <MetasMes onOpen={() => onOpenMeta ? onOpenMeta() : onNav("reports")} />
          <AlertasProximos
            onOpenAlerts={() => onNav("alerts")}
            onOpenLead={onOpenLead}
            onCreateAlert={onCreateAlert} />
          
        </div>
      </div>
    </div>);

}

export { ResumoPage, KPIStat };